import { Branch } from "./useExcludedBranch";
import { trueDepthBelow } from "./hierarchyDepth";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;

export type BranchedNode = QuickFilterNodeDTO & { branch: Branch };

export const nodeAtBranch = (
	root: QuickFilterNodeDTO | null,
	branch: Branch
): QuickFilterNodeDTO | null => {
	let node = root;

	for (const index of branch) {
		node = node?.children?.[index] ?? null;

		if (!node) {
			return null;
		}
	}

	return node;
};

export const branchOfLabelAndLevel = (
	root: QuickFilterNodeDTO | null,
	level: number | null,
	label: string | null
): Branch | null => {
	if (!root) {
		return null;
	}

	if (
		(level === null || root.level === level) &&
		(label === null || root.label === label)
	) {
		return [];
	}

	if (root.level === level) {
		return null;
	}

	for (const [index, child] of (root.children ?? []).entries()) {
		const found = branchOfLabelAndLevel(child, level, label);

		if (found) {
			return [index, ...found];
		}
	}

	return null;
};

export const pathFromRoot = (
	root: QuickFilterNodeDTO | null,
	branch: Branch
): BranchedNode[] => {
	if (!root) {
		return [];
	}

	const path: BranchedNode[] = [{ ...root, branch: [] }];
	let node: QuickFilterNodeDTO | null = root;

	for (const [depth, index] of branch.entries()) {
		node = node?.children?.[index] ?? null;

		if (!node) {
			break;
		}

		path.push({ ...node, branch: branch.slice(0, depth + 1) });
	}

	return path;
};

export const childrenOfBranch = (
	root: QuickFilterNodeDTO | null,
	branch: Branch
): BranchedNode[] => {
	const node = nodeAtBranch(root, branch);

	return (node?.children ?? []).map((child, index) => ({
		...child,
		branch: [...branch, index],
	}));
};

export const isExpandable = (node: QuickFilterNodeDTO): boolean =>
	trueDepthBelow(node) > 0;

export const branchKey = (branch: Branch): string => JSON.stringify(branch);

export const isWithin = (branch: Branch, ancestor: Branch): boolean =>
	ancestor.every((index, depth) => branch[depth] === index);

export type CrumbTrail<T> = {
	lead: T | null;
	hidden: T[];
	tail: T[];
};

export const collapseCrumbs = <T>(
	crumbs: T[],
	maxVisible = 4
): CrumbTrail<T> => {
	if (crumbs.length <= maxVisible) {
		return { lead: null, hidden: [], tail: crumbs };
	}

	return {
		lead: crumbs[0],
		hidden: crumbs.slice(1, -2),
		tail: crumbs.slice(-2),
	};
};
